import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

// Material UI components
import { Box, Drawer, Button, Typography, IconButton, Popover } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";

// components
import CustomIcon from "components/CustomIcon";
import CTA from "components/Pro/CTA";
import { TagName } from "components/TagName";
import SearchInput from "components/SearchInput";

// Store
import { fetchSession } from "store/slice/auth";
import { toggleLoader } from "store/slice/UI";

// Utils
import { useAuth } from "utils";

// Hooks
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Services
import { logout } from "services/auth.service";
import { updateUserOrg, UserOrgs } from "services";

// Types
import { Pagination } from "types";
import { ILoggedInUserData } from "store/slice/auth/authSlice.types";

// Styles
import styles from "./SideBar.styles";
import icons from "enums/icons";

// Resources
import lcapLogo from "assets/images/svgs/lcap-official.svg";

// Enum
import { urlPaths } from "enums/urlPaths";
import { fetchUserOrgsThunk, getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { MainMenu } from "../MainMenu";
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

interface SideBarProps {
    pathname: string;
    loggedInUserData: ILoggedInUserData;
}

type Hubs = {
    data: UserOrgs[];
    meta: Pagination;
    status: RequestInfo;
};

const HubAdmin = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const classes = styles.sideBarStyles();

    const { sessionData } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const userOrganizations = useAppSelector(getUserOrgs);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [userOrgs, setUserOrgs] = useState<Hubs>({
        data: userOrganizations.data,
        meta: userOrganizations.pagination,
        status: userOrganizations.status,
    });

    const [filterValue, setFilterValue] = useState("");
    const filteredHubs = userOrgs.data.filter((hub) => hub.name.toLowerCase().includes(filterValue.toLowerCase()));

    const selectedOrg = sessionData?.authn.user.org_selected;
    const shouldSeeCurrentHub = userOrganizations.data.some((org) => org.user.function_name !== "PROFILE");

    const handleOpenHubSelector = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRedirectUserOrgUpdate = () => {
        if (pathname.includes(`/leadership-dynamics/`)) {
            navigate(urlPaths.MySearches);
        }

        if (pathname.includes(`/pace/`) || pathname.includes(urlPaths.UserManagement)) {
            navigate(urlPaths.PaceLabsDashboard);
        }

        if (pathname.includes(urlPaths.HubManagementAddUsers) || pathname.includes(urlPaths.HubManagementEditClient)) {
            navigate(urlPaths.HubManagement);
        }
    };

    const handleUpdateSelectedOrg = async (orgId: number) => {
        handleClose();
        setFilterValue("");
        try {
            dispatch(toggleLoader(true));
            const { data } = await updateUserOrg(orgId);
            if (data.Info === "OK") {
                await dispatch(fetchSession());
                handleRedirectUserOrgUpdate();
            }
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleLoader(false));
        }
    };

    const handleFilterHub = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterValue(e.target.value);
    };

    useEffect(() => {
        const initialFetch = async () => {
            try {
                setUserOrgs((prevState) => ({ ...prevState, status: "fetching" }));
                const data = unwrapResult(await dispatch(fetchUserOrgsThunk()));
                setUserOrgs({ data: data.results, meta: data.meta, status: "done" });
            } catch (error) {
                console.error(error);
            } finally {
                setUserOrgs((prevState) => ({ ...prevState, status: "done" }));
            }
        };

        if (userOrganizations.data.length && userOrganizations.status === "done") {
            setUserOrgs({
                data: userOrganizations.data,
                meta: userOrganizations.pagination,
                status: userOrganizations.status,
            });
        }

        if (!userOrganizations.data.length && userOrganizations.status === "pristine") {
            initialFetch();
        }
    }, [dispatch, userOrganizations]);

    return selectedOrg?.id && shouldSeeCurrentHub ? (
        <Box className={classes.hubSelector}>
            <Typography className={classes.hubSelectorTitle}>
                {intl.formatMessage({ id: "sidebar.sysadmin.yourCurrentHub" })}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <TagName color="secondary">{selectedOrg?.name ?? ""}</TagName>
                {Boolean(userOrgs.data.length > 1) && (
                    <IconButton className={classes.hubSelectorTrigger} onClick={handleOpenHubSelector}>
                        <CustomIcon type="outlined" icon={icons.chevronDownThin} />
                    </IconButton>
                )}
                <Popover
                    id="HubSelectorPopover"
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    classes={{ paper: classes.hubSelectorPopoverPaper }}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Box className={classes.hubSelectorMenu}>
                        <Typography className={classes.hubSelectorMenuTitle}>
                            {intl.formatMessage({ id: "sidebar.sysadmin.switchHubs" })}
                        </Typography>
                        <SearchInput
                            visualVariant="sidebarHub"
                            placeholder="Filter Hub"
                            onChange={handleFilterHub}
                            value={filterValue}
                        />
                        <Box className={classes.hubSelectorMenuList}>
                            {filteredHubs
                                .filter((hub) => selectedOrg?.id !== hub.id)
                                .map((hub) => (
                                    <Box key={hub.id} className={classes.hubSelectorMenuItem}>
                                        <IconButton onClick={() => handleUpdateSelectedOrg(hub.id)}>
                                            <TagName>{hub.name}</TagName>
                                        </IconButton>
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                </Popover>
            </Box>
        </Box>
    ) : (
        <></>
    );
};

const MenuInfo = ({ name, pathname }: { name: string; pathname: string }) => {
    const intl = useIntl();
    const classes = styles.sideBarStyles();

    const ability = useAbility(AbilityContext);

    const { sessionData } = useAuth();
    const userOrganizations = useAppSelector(getUserOrgs);

    const selectedOrg = sessionData?.authn.user.org_selected;
    const currentOrgData = userOrganizations.data.find((org) => org.id === selectedOrg?.id);

    const currentFunct = currentOrgData?.user.function_name;
    const isCurrentFunctProfile = currentFunct === "PROFILE";
    const isAdmin = ability.can("see", "SYS_ADMIN") || ability.can("see", "SYS_ADMIN_2");

    const showGoPro = userOrganizations.status === "done" && (isCurrentFunctProfile || !currentFunct) && !isAdmin;

    return (
        <Box className={classes.userSettingsWrapper}>
            <Box className={classes.userSettingsUserNameWrapper}>
                <Link className={classes.userSettingsLink} to={urlPaths.AccountManagement}>
                    <CustomIcon icon={icons.userMenu} />
                    <Typography>{name}</Typography>
                </Link>
                {showGoPro && (
                    <CTA text={intl.formatMessage({ id: "pro.message.button.goPro" })} variant="message" onSideBar />
                )}
            </Box>
            <Button className={classes.userSettingsLink} onClick={logout}>
                <LogoutIcon />
                <Typography>{intl.formatMessage({ id: "button.logout" })}</Typography>
            </Button>
        </Box>
    );
};

const SideBar = ({ pathname, loggedInUserData }: SideBarProps): JSX.Element => {
    const intl = useIntl();
    const classes = styles.sideBarStyles();

    const shouldShowSidebarMenu = !pathname.includes(urlPaths.ChooseYourHub);

    return (
        <Drawer className={classes.drawer} variant="persistent" anchor="left" open>
            <Link className={classes.mainLogo} to="/pace/my-dashboard">
                <img src={lcapLogo} alt={intl.formatMessage({ id: "main-menu.logo" })} />
            </Link>

            {shouldShowSidebarMenu && (
                <>
                    <HubAdmin />
                    <MainMenu />
                    <MenuInfo name={loggedInUserData.name} pathname={pathname} />
                </>
            )}
        </Drawer>
    );
};

export default SideBar;
